import React, { useState, useEffect, useCallback } from 'react';
import {
  ChatBubbleLeftRightIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  SparklesIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  queryFeedback,
  getFeedbackStats,
  reviewFeedback,
  bulkReviewFeedback,
  generateFeedbackRules,
  getCuratedRules,
  saveCuratedRules,
  toggleCuratedRule,
  deleteCuratedRule,
  getOverrideAnalytics,
} from '../services/api';

const PIPELINE_LABELS = {
  classification: 'Classification',
  tearline: 'Tearline',
  foia: 'FOIA',
};

const DECISION_BADGES = {
  accept: { label: 'Accepted', class: 'bg-green-100 text-green-800' },
  reject: { label: 'Rejected', class: 'bg-red-100 text-red-800' },
  modify: { label: 'Modified', class: 'bg-amber-100 text-amber-800' },
  override: { label: 'Override', class: 'bg-purple-100 text-purple-800' },
  skip: { label: 'Skipped', class: 'bg-gray-100 text-gray-600' },
};

const REVIEW_BADGES = {
  pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  approved: { label: 'Approved', class: 'bg-green-100 text-green-800 border-green-300' },
  rejected: { label: 'Rejected', class: 'bg-red-100 text-red-800 border-red-300' },
};

export default function FeedbackManagement() {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPipeline, setSelectedPipeline] = useState('classification');
  const [selectedReviewStatus, setSelectedReviewStatus] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [selected, setSelected] = useState(new Set());

  // Curated rules state
  const [curatedRules, setCuratedRules] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedRules, setGeneratedRules] = useState(null);
  const [editingRules, setEditingRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' | 'rules'

  const loadStats = async () => {
    try {
      const data = await getFeedbackStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await queryFeedback(selectedPipeline, {
        reviewStatus: selectedReviewStatus || undefined,
        limit: 200,
      });
      setEntries(data.results || []);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPipeline, selectedReviewStatus]);

  const loadRules = async () => {
    try {
      const data = await getCuratedRules(selectedPipeline, false);
      setCuratedRules(data.rules || []);
    } catch (err) {
      console.error('Failed to load rules:', err);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadRules(); }, [selectedPipeline]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const pendingIds = entries.filter(e => (e.review_status || 'pending') === 'pending').map(e => e.feedback_id);
    setSelected(new Set(pendingIds));
  };

  // Admin review actions
  const handleReview = async (feedbackId, status) => {
    try {
      await reviewFeedback(feedbackId, selectedPipeline, status);
      await loadEntries();
      await loadStats();
    } catch (err) {
      console.error('Review failed:', err);
    }
  };

  const handleBulkReview = async (status) => {
    if (selected.size === 0) return;
    try {
      await bulkReviewFeedback(Array.from(selected), selectedPipeline, status);
      setSelected(new Set());
      await loadEntries();
      await loadStats();
    } catch (err) {
      console.error('Bulk review failed:', err);
    }
  };

  // Rule generation
  const handleGenerateRules = async () => {
    setGenerating(true);
    try {
      const result = await generateFeedbackRules(selectedPipeline);
      if (result.success) {
        setGeneratedRules(result);
        setEditingRules(result.rules.map(r => ({ ...r })));
        setActiveTab('rules');
      } else {
        alert(result.message || 'No approved corrections to synthesize.');
      }
    } catch (err) {
      console.error('Rule generation failed:', err);
      alert('Rule generation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveRules = async () => {
    if (!editingRules || editingRules.length === 0) return;
    setSaving(true);
    try {
      await saveCuratedRules(editingRules);
      setEditingRules(null);
      setGeneratedRules(null);
      await loadRules();
      await loadStats();
    } catch (err) {
      console.error('Save rules failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId, active) => {
    try {
      await toggleCuratedRule(ruleId, active);
      await loadRules();
      await loadStats();
    } catch (err) {
      console.error('Toggle rule failed:', err);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteCuratedRule(ruleId);
      await loadRules();
      await loadStats();
    } catch (err) {
      console.error('Delete rule failed:', err);
    }
  };

  const pipelineStats = stats?.by_pipeline || {};
  const reviewStats = stats?.by_review_status || {};
  const activeRuleCount = stats?.active_rules || 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Feedback Management</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            Review analyst corrections, approve them, then generate curated rules for prompt injection
          </p>
        </div>
        <button onClick={() => { loadStats(); loadEntries(); loadRules(); }}
          className="px-3 py-1.5 text-sm border border-[var(--border)] rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] flex items-center gap-1">
          <ArrowPathIcon className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border)] p-3 text-center">
            <div className="text-2xl font-bold text-[var(--text-primary)]">{stats.total || 0}</div>
            <div className="text-xs text-[var(--text-secondary)]">Total</div>
          </div>
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-yellow-600/30 p-3 text-center">
            <div className="text-2xl font-bold text-yellow-500">{reviewStats.pending || 0}</div>
            <div className="text-xs text-[var(--text-secondary)]">Pending Review</div>
          </div>
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-green-600/30 p-3 text-center">
            <div className="text-2xl font-bold text-green-500">{reviewStats.approved || 0}</div>
            <div className="text-xs text-[var(--text-secondary)]">Approved</div>
          </div>
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-red-600/30 p-3 text-center">
            <div className="text-2xl font-bold text-red-500">{reviewStats.rejected || 0}</div>
            <div className="text-xs text-[var(--text-secondary)]">Rejected</div>
          </div>
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-indigo-600/30 p-3 text-center">
            <div className="text-2xl font-bold text-indigo-400">{activeRuleCount}</div>
            <div className="text-xs text-[var(--text-secondary)]">Active Rules</div>
          </div>
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border)] p-3 text-center">
            <div className="text-2xl font-bold text-[var(--text-primary)]">{stats.curated_rules || 0}</div>
            <div className="text-xs text-[var(--text-secondary)]">Total Rules</div>
          </div>
        </div>
      )}

      {/* Tabs: Feedback vs Rules */}
      <div className="flex items-center gap-4 mb-4 border-b border-[var(--border)]">
        <button onClick={() => setActiveTab('feedback')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'feedback' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          Feedback Entries
        </button>
        <button onClick={() => setActiveTab('rules')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'rules' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          Curated Rules ({activeRuleCount} active)
        </button>
        <button onClick={() => setActiveTab('analytics')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'analytics' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          Override Analytics
        </button>
      </div>

      {activeTab === 'analytics' && <OverrideAnalyticsPanel />}

      {activeTab === 'feedback' && (
        <>
          {/* Pipeline + review status filters */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4 text-[var(--text-secondary)]" />
              {Object.entries(PIPELINE_LABELS).map(([key, label]) => (
                <button key={key} onClick={() => { setSelectedPipeline(key); setSelectedReviewStatus(''); setSelected(new Set()); }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectedPipeline === key ? 'bg-indigo-900/40 border-indigo-500 text-indigo-300' : 'bg-[var(--bg-primary)] border-[var(--border)] text-[var(--text-secondary)]'}`}>
                  {label} ({pipelineStats[key]?.total || 0})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-[var(--text-secondary)] mr-1">Status:</span>
              {['', 'pending', 'approved', 'rejected'].map(s => (
                <button key={s} onClick={() => setSelectedReviewStatus(s)}
                  className={`px-2 py-0.5 text-xs rounded border transition-colors ${selectedReviewStatus === s ? 'bg-indigo-900/40 border-indigo-500 text-indigo-300' : 'bg-[var(--bg-primary)] border-[var(--border)] text-[var(--text-secondary)]'}`}>
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk actions bar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-xs text-indigo-400 hover:underline">Select all pending</button>
              {selected.size > 0 && (
                <>
                  <span className="text-xs text-[var(--text-secondary)]">({selected.size} selected)</span>
                  <button onClick={() => handleBulkReview('approved')}
                    className="px-2 py-1 text-xs bg-green-700 text-white rounded hover:bg-green-600 flex items-center gap-1">
                    <CheckCircleIcon className="w-3 h-3" /> Approve Selected
                  </button>
                  <button onClick={() => handleBulkReview('rejected')}
                    className="px-2 py-1 text-xs bg-red-700 text-white rounded hover:bg-red-600 flex items-center gap-1">
                    <XCircleIcon className="w-3 h-3" /> Reject Selected
                  </button>
                </>
              )}
            </div>
            <button onClick={handleGenerateRules} disabled={generating}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
              <SparklesIcon className="w-4 h-4" />
              {generating ? 'Generating...' : 'Generate Rules from Approved'}
            </button>
          </div>

          {/* Entries list */}
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border)]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin h-6 w-6 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto" />
              </div>
            ) : entries.length === 0 ? (
              <div className="p-12 text-center">
                <ChatBubbleLeftRightIcon className="w-10 h-10 text-[var(--text-secondary)] mx-auto mb-2 opacity-50" />
                <p className="text-[var(--text-secondary)]">No feedback entries for this filter.</p>
              </div>
            ) : (
              <div>
                <div className="px-4 py-2 border-b border-[var(--border)] text-xs text-[var(--text-secondary)]">
                  {entries.length} entries
                </div>
                {entries.map((entry, idx) => {
                  const isExpanded = expanded.has(entry.feedback_id);
                  const isSelected = selected.has(entry.feedback_id);
                  const badge = DECISION_BADGES[entry.decision] || DECISION_BADGES.skip;
                  const reviewBadge = REVIEW_BADGES[entry.review_status || 'pending'];
                  return (
                    <div key={entry.feedback_id || idx} className="border-b border-[var(--border)] last:border-b-0">
                      <div className="px-4 py-3 flex items-center gap-3 hover:bg-[var(--bg-primary)] transition-colors">
                        {/* Checkbox */}
                        <input type="checkbox" checked={isSelected}
                          onChange={() => toggleSelect(entry.feedback_id)}
                          className="w-4 h-4 rounded border-gray-500" />

                        {/* Content - clickable to expand */}
                        <div onClick={() => toggleExpand(entry.feedback_id)}
                          className="flex items-center justify-between flex-1 cursor-pointer min-w-0">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`px-2 py-0.5 text-xs rounded border ${reviewBadge.class}`}>
                              {reviewBadge.label}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded ${badge.class}`}>
                              {badge.label}
                            </span>
                            <span className="text-sm text-[var(--text-primary)] truncate max-w-lg">
                              {entry.user_reasoning || entry.document_snippet?.slice(0, 80) || 'No reasoning'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 text-xs text-[var(--text-secondary)]">
                            {entry.confidence != null && <span>{(entry.confidence * 100).toFixed(0)}%</span>}
                            <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}</span>
                          </div>
                        </div>

                        {/* Admin actions */}
                        {(entry.review_status || 'pending') === 'pending' && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => handleReview(entry.feedback_id, 'approved')}
                              className="p-1 text-green-500 hover:bg-green-900/30 rounded" title="Approve">
                              <CheckCircleIcon className="w-5 h-5" />
                            </button>
                            <button onClick={() => handleReview(entry.feedback_id, 'rejected')}
                              className="p-1 text-red-500 hover:bg-red-900/30 rounded" title="Reject">
                              <XCircleIcon className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-1 bg-[var(--bg-primary)] border-t border-[var(--border)]">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {entry.document_snippet && (
                              <div className="col-span-2">
                                <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">Document Context</div>
                                <div className="bg-[var(--bg-secondary)] rounded p-2 text-xs text-[var(--text-primary)] max-h-24 overflow-y-auto whitespace-pre-wrap">
                                  {entry.document_snippet}
                                </div>
                              </div>
                            )}
                            {entry.original_value && (
                              <div>
                                <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">System Classification</div>
                                <div className="bg-[var(--bg-secondary)] rounded p-2 text-xs font-mono text-[var(--text-primary)]">
                                  {JSON.stringify(entry.original_value, null, 2)}
                                </div>
                              </div>
                            )}
                            {entry.corrected_value && (
                              <div>
                                <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">Analyst Correction</div>
                                <div className="bg-[var(--bg-secondary)] rounded p-2 text-xs font-mono text-[var(--text-primary)]">
                                  {JSON.stringify(entry.corrected_value, null, 2)}
                                </div>
                              </div>
                            )}
                            {entry.user_reasoning && (
                              <div className="col-span-2">
                                <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">Analyst Reasoning</div>
                                <div className="bg-[var(--bg-secondary)] rounded p-2 text-sm text-[var(--text-primary)]">
                                  {entry.user_reasoning}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-secondary)]">
                            <span>ID: {entry.feedback_id?.slice(0, 8)}</span>
                            <span>Hash: {entry.document_hash}</span>
                            {entry.reviewed_by && <span>Reviewed by: {entry.reviewed_by}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'rules' && (
        <div className="space-y-4">
          {/* Generated rules editor (shown after Generate Rules) */}
          {editingRules && (
            <div className="bg-[var(--bg-secondary)] rounded-lg border border-indigo-500/50 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-indigo-400 flex items-center gap-1.5">
                  <SparklesIcon className="w-4 h-4" />
                  Generated Rules — Review & Edit Before Saving
                </h3>
                <span className="text-xs text-[var(--text-secondary)]">
                  From {generatedRules?.source_corrections || 0} approved corrections
                </span>
              </div>
              <div className="space-y-2 mb-4">
                {editingRules.map((rule, idx) => (
                  <div key={rule.rule_id || idx} className="flex items-start gap-2">
                    <span className="text-xs text-[var(--text-secondary)] mt-2 w-6 flex-shrink-0">{idx + 1}.</span>
                    <textarea
                      value={rule.rule_text}
                      onChange={(e) => {
                        const updated = [...editingRules];
                        updated[idx] = { ...updated[idx], rule_text: e.target.value };
                        setEditingRules(updated);
                      }}
                      className="flex-1 text-sm bg-[var(--bg-primary)] border border-[var(--border)] rounded p-2 text-[var(--text-primary)] resize-none"
                      rows={2}
                    />
                    <button onClick={() => setEditingRules(editingRules.filter((_, i) => i !== idx))}
                      className="p-1 text-red-400 hover:text-red-300 mt-1" title="Remove rule">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveRules} disabled={saving}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Activate Rules'}
                </button>
                <button onClick={() => { setEditingRules(null); setGeneratedRules(null); }}
                  className="px-4 py-2 text-sm border border-[var(--border)] rounded-md text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]">
                  Discard
                </button>
              </div>
            </div>
          )}

          {/* Active curated rules */}
          <div className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border)]">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
              <h3 className="text-sm font-medium text-[var(--text-primary)]">
                Active Classification Guidance Rules
              </h3>
              <span className="text-xs text-[var(--text-secondary)]">
                These rules are injected into LLM prompts for {PIPELINE_LABELS[selectedPipeline] || selectedPipeline}
              </span>
            </div>
            {curatedRules.length === 0 ? (
              <div className="p-8 text-center">
                <SparklesIcon className="w-8 h-8 text-[var(--text-secondary)] mx-auto mb-2 opacity-50" />
                <p className="text-[var(--text-secondary)]">No curated rules yet.</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Approve feedback entries, then click "Generate Rules from Approved" to create them.
                </p>
              </div>
            ) : (
              <div>
                {curatedRules.map((rule, idx) => (
                  <div key={rule.rule_id || idx}
                    className={`px-4 py-3 border-b border-[var(--border)] last:border-b-0 flex items-start gap-3 ${!rule.active ? 'opacity-50' : ''}`}>
                    <span className="text-xs text-[var(--text-secondary)] mt-1 w-6 flex-shrink-0">{idx + 1}.</span>
                    <div className="flex-1">
                      <p className="text-sm text-[var(--text-primary)]">{rule.rule_text}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-secondary)]">
                        <span className={rule.active ? 'text-green-500' : 'text-red-400'}>{rule.active ? 'Active' : 'Inactive'}</span>
                        {rule.created_at && <span>{new Date(rule.created_at).toLocaleDateString()}</span>}
                        {rule.source_feedback_ids && <span>{rule.source_feedback_ids.length} source corrections</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleToggleRule(rule.rule_id, !rule.active)}
                        className={`p-1 rounded ${rule.active ? 'text-yellow-500 hover:bg-yellow-900/30' : 'text-green-500 hover:bg-green-900/30'}`}
                        title={rule.active ? 'Deactivate' : 'Activate'}>
                        {rule.active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleDeleteRule(rule.rule_id)}
                        className="p-1 text-red-400 hover:bg-red-900/30 rounded" title="Delete">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pipeline filter for rules */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--text-secondary)]">Pipeline:</span>
            {Object.entries(PIPELINE_LABELS).map(([key, label]) => (
              <button key={key} onClick={() => setSelectedPipeline(key)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectedPipeline === key ? 'bg-indigo-900/40 border-indigo-500 text-indigo-300' : 'bg-[var(--bg-primary)] border-[var(--border)] text-[var(--text-secondary)]'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function OverrideAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getOverrideAnalytics()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-[var(--text-secondary)] mt-4">Loading analytics…</div>;
  if (error) return <div className="mt-4 bg-red-900/30 border border-red-700 text-red-300 rounded p-3">{error}</div>;
  if (!data) return null;

  return (
    <div className="mt-4 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-3">
          <div className="text-xs text-[var(--text-secondary)] uppercase">Total overrides</div>
          <div className="text-2xl font-bold text-[var(--text-primary)]">{data.total_overrides}</div>
        </div>
        {Object.entries(data.by_pipeline || {}).map(([k, v]) => (
          <div key={k} className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-3">
            <div className="text-xs text-[var(--text-secondary)] uppercase">{k}</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Most overridden rules</h3>
          {(data.most_overridden_rules || []).length === 0 && <div className="text-xs text-[var(--text-secondary)]">No data yet.</div>}
          {(data.most_overridden_rules || []).map((r) => (
            <div key={r.rule_id} className="flex justify-between items-center text-sm py-1 border-b border-[var(--border)] last:border-0">
              <span className="font-mono text-xs text-[var(--text-primary)]">{r.rule_id}</span>
              <span className="font-semibold text-amber-400">{r.override_count}</span>
            </div>
          ))}
        </div>

        <div className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Top correcting analysts</h3>
          {(data.top_correcting_analysts || []).length === 0 && <div className="text-xs text-[var(--text-secondary)]">No data yet.</div>}
          {(data.top_correcting_analysts || []).map((a) => (
            <div key={a.analyst_id} className="flex justify-between items-center text-sm py-1 border-b border-[var(--border)] last:border-0">
              <span className="text-[var(--text-primary)]">{a.user_id}</span>
              <span className="font-semibold text-indigo-400">{a.corrections}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-md p-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Level change distribution</h3>
        {Object.keys(data.level_change_distribution || {}).length === 0 && <div className="text-xs text-[var(--text-secondary)]">No overrides with level changes yet.</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.level_change_distribution || {}).map(([k, v]) => (
            <div key={k} className="bg-[var(--bg-primary)] rounded px-2 py-1 text-xs flex justify-between">
              <span className="font-mono text-[var(--text-primary)]">{k}</span>
              <span className="font-semibold text-indigo-400">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
